import { sectionsStateStore } from "@/library/stores/SectionsStateStore";
import { defineComponent, type PropType } from "vue";
import type { INavigationStep, ISectionConfig, ISectionState } from "@/types/ComponentLibrary/component-library";
import VRuntimeTemplate from "vue3-runtime-template";
import { isControl, isTreeSection } from "@/helpers/Misc";
import type { IValidatorResult } from "@/types/FormEngine/validation";
import { t } from "@/services/Translation/t";
/**
 * @component
 */
export default defineComponent({
  name: "AccordionSection",
  components: {
    VRuntimeTemplate,
  },
  props: {
    identifier: { default: "" },
    index: { default: 1 },
    level: { default: 1 },
    mtdt: {
      type: Object as PropType<ITreeSection>,
      required: true,
    },
  },
  created() {
    window.FormEngine.form.registerSection(this.identifier, this);
  },
  data() {
    let store = sectionsStateStore(this.identifier as string);
    let parent = window.FormEngine.form.sections.get(this.mtdt.Parent);

    if (!parent && this.level > 1) {
      parent = window.FormEngine.form.controls.get(this.mtdt.Parent);
      if (!parent) {
        console.log("Doesn't have a parent :( ", this.identifier, this.mtdt.Parent);
      }
    }
    if (store.reload) {
      store.state = {
        indicator: "unopened",
        visited: false,
        isValid: true,
        hiddenHint: true,
        hidden: false,
        isOpen: this.mtdt.HasHiddenCaption ? true : false,
        titleHidden: this.mtdt.HasHiddenCaption ? this.mtdt.HasHiddenCaption : false,
        descriptionHidden: !this.mtdt.Title ? true : false,
        skipValidation: false,
        parent: parent as any,
        controls: new Array<any>(),
        additional:{}
      } as ISectionState;
      store.config = {
        classes: this.mtdt.Classes,
      } as ISectionConfig;
    }
    store.reload = false;
    return {
      store: store,
      state: store.state as ISectionState,
      config: store.config as ISectionConfig,
    };
  },
  computed: {
    additionalData(): any {
      return this.state.additional;
    },
    displayTitle(): string {
      return t("Name", this.identifier);
    },
    isWizardStep(): boolean {
      return window.FormEngine.engineConfig.builder && window.FormEngine.engineConfig.builder.Wizard && this.level == 1;
    },
    displayDescription(): string {
      return t("Title", this.identifier);
    },
    descriptionHidden(): boolean {
      return this.state.descriptionHidden;
    },
    displayHint(): string {
      return t("Hint", this.identifier);
    },
    displayHintText(): string {
      return this.state.hiddenHint ? t("displayHintTextClosed", "_utility") : t("displayHintTextOpened", "_utility");
    },
    displayHintClasses(): string {
      return "govuk-display-hint " + (this.state.hiddenHint ? "hint-closed" : "hint-opened");
    },
    isVisited(): boolean {
      return this.state.visited;
    },
    isValid(): boolean {
      let validity = true;
      window.FormEngine.form.walkTreeWith(
        this.mtdt.Tree,
        function (control: any) {
          if (!control.state.isValid) {
            validity = false;
          }
        },
        function (section: any) {
          if (!section.state.isValid) {
            validity = false;
          }
        }, (element:any) => {
          return !element.state.hidden;
        }
      );
      return validity;
    },
    sectionClasses(): string {
      return (
        (this.config.classes ? this.config.classes : "") +
        " govuk-accordion__section " +
        (this.state.hidden ? "hidden " : "") +
        (this.state.isOpen ? "govuk-accordion__section-expanded " : "") +
        (this.state.visited && !this.state.titleHidden ? "unopened " : "visited ")
      );
    },
    indicator(): string {
      if (this.state.isOpen) {
        return "editing";
      } else {
        if (this.state.visited) {
          if (this.isValid) {
            return "valid";
          } else {
            return "invalid";
          }
        }
      }
      return "unopened";
    },
    sectionIndicatorClasses(): string {
      let classes = "section-indicator ";
      switch (this.indicator) {
        case "editing":
          classes = classes + "fa-pen";
          break;
        case "valid":
          classes = classes + "fa-check";
          break;
        case "invalid":
          classes = classes + "fa-exclamation";
          break;
        case "unopened":
        default:
          classes = classes + "unopened fa-pen";
          break;
      }
      return classes;
    },
    titleId(): string {
      return this.identifier + "-heading-" + this.level;
    },
    descriptionId(): string {
      return this.identifier + "-summary-" + this.level;
    },
    contentId(): string {
      return this.identifier + "-content-" + this.level;
    },
    hintId() {
      return this.identifier + "-hint";
    },
    isEmptySection() {
      let isSectionEmpty = true;
      window.FormEngine.form.walkTreeWith(this.mtdt.Tree, function (control: any) {
        if (control.getValue() !== "" && control.getValue() !== null && control.getValue().length !== 0) {
          isSectionEmpty = false;
        }
      });
      return isSectionEmpty as boolean;
    },
  },
  methods: {
    toggleHintSection() {
      this.state.hiddenHint = !this.state.hiddenHint;
    },
    subscribe(event: string, callback: Function, specifier?: string) {
      window.FormEngine.events.subscribe(event, callback, this.identifier, specifier, null);
    },
    hide() {
      //TODO: prerobit na walkTreeWith funkciu aby asa schovalo vsetko (pokial nenarazi na sekciu tu hidne samo) / tabulky hide aj detailu
      this.state.hidden = true;
      let controls = window.FormEngine.form.getControls(this.identifier);
      for (let control of controls) {
        control.hide();
      }
    },
    show() {
      this.state.hidden = false;
      let controls = window.FormEngine.form.getControls(this.identifier);
      for (let control of controls) {
        control.show();
      }
    },
    validate() {
      let validity = true;
      this.state.visited = true;
      let validationResults: Array<IValidatorResult> = [];

      window.FormEngine.form.walkTreeWith(
        this.mtdt.Tree,
        function (control: any) {
          if (!control.state.hidden) {
            validationResults.push(control.validate(control.fillErrorBagComputed));
            if (!control.state.isValid) {
              //console.log("validity problem");
              validity = false;
            }
          }
        },
        function (section: any) {
          if (!section.state.hidden) {
            validationResults.push(section.validate());
            if (!section.state.isValid) {
              validity = false;
            }
          }
        },
        function (sectionWithTree: any) {
          return false;
        }
      );
      //console.log(validity);
      this.state.isValid = validity;
      return validationResults;
    },
    toggleAccordionTab() {
      if (this.state.titleHidden) {
        return;
      }
      if (this.state.isOpen) {
        this.closeAccordionTab();
      } else {
        this.openAccordionTab(false);
      }
    },
    reset(silent: boolean = true, propagateDown: boolean = true) {
      this.state.visited = false;
      this.state.isValid = true;
      this.state.hidden = this.mtdt.IsOptionSection ? true : false;
      this.state.isOpen = this.mtdt.HasHiddenCaption ? true : false;
      this.state.titleHidden = this.mtdt.HasHiddenCaption ? this.mtdt.HasHiddenCaption : false;
      this.state.descriptionHidden = !this.mtdt.Title ? true : false;
      this.state.skipValidation = false;
      if (propagateDown) {
        window.FormEngine.form.walkTreeWith(
          this.mtdt.Tree,
          function (control: any) {
            if (isControl(control.mtdt)) {
              control.reset(silent);
              control.resetErrors();
            }
          }
        );
      }
    },
    touch(propagateUp: boolean) {
      this.state.visited = true;
      if (this.state.parent && propagateUp) {
        this.state.parent.touch(propagateUp);
      }
    },
    resetErrors() {
      for (let elementId in this.mtdt.Tree) {
        //console.log(elementId);
        let element = window.FormEngine.form.getTargetable(elementId);
        if (element) {
          element.resetErrors();
        }
      }
    },
    resetRequired(walkDeeper: boolean = false) {
      window.FormEngine.form.walkTreeWith(
        this.mtdt.Tree,
        function (control: any) {
          if (isControl(control.mtdt)) {
            control.resetRequired();
          }
        },
        null,
        () => {
          return walkDeeper;
        }
      );
    },
    setRequired(required: boolean = true, walkDeeper: boolean = false) {
      window.FormEngine.form.walkTreeWith(
        this.mtdt.Tree,
        function (control: any) {
          if (isControl(control.mtdt)) {
            control.setRequired(required);
          }
        },
        null,
        () => {
          return walkDeeper;
        }
      );
    },
    openAccordionTab(propagateUp: boolean = true) {
      this.state.visited = true;
      this.state.isOpen = true;
      window.FormEngine.events.publish(window.FormEngine.events.list.SECTION_OPEN, this, this.identifier);
      if (this.mtdt.Parent && propagateUp) {
        let section = window.FormEngine.form.sections.get(this.mtdt.Parent);
        if (!section) {
          section = window.FormEngine.form.controls.get(this.mtdt.Parent);
          if (section === undefined) {
            return;
          } else {
            //TODO: opening Table rows(detail)
            //console.log("openAccordionTab could not find parent of :", this.identifier);
          }
        } else {
          section.openAccordionTab();
        }
      }
      //window.FormEngine.events.publish(window.FormEngine.events.list.SECTION_OPEN, this, this.identifier);
    },
    closeAccordionTab(skipValidation: boolean = false) {
      this.state.isOpen = false;
      this.state.skipValidation = skipValidation;
      window.FormEngine.events.publish(window.FormEngine.events.list.SECTION_CLOSE, this, this.identifier);
    },
    getNavigationData(): [INavigationStep] {
      if (!this.state.parent) {
        return [
          {
            identifier: this.identifier,
          } as INavigationStep,
        ];
      }
      let navigationData = this.state.parent.getNavigationData();
      navigationData.push({
        identifier: this.identifier,
      } as INavigationStep);
      return navigationData;
    },
    getTag(level: Number) {
      switch (level) {
        case 1:
          return { tag: "h1", class: "govuk-heading-xl" };
        case 2:
          return { tag: "h2", class: "govuk-heading-l" };
        case 3:
          return { tag: "h3", class: "govuk-heading-m" };
        case 4:
          return { tag: "h4", class: "govuk-heading-s" };
        case 5:
          return { tag: "h5", class: "govuk-heading-s" };
        default:
          return { tag: "div", class: "govuk-heading-l" };
      }
    },
  },
  t
});
